import React, { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import {
  User,
  Bell,
  Clock,
  Sun,
  Moon,
  Smartphone,
  Share2,
  Download,
  FileSpreadsheet,
  FileText,
  Trash2,
  RefreshCw,
  Info,
  Check,
  Camera,
  LogOut,
  ShieldCheck,
  ChevronRight,
  Database,
  AlertTriangle,
  X,
  ChevronUp,
  ChevronDown,
  Bug,
  Cpu,
  Sliders,
  Sparkles,
  Key,
  CheckCircle2,
  ExternalLink,
} from 'lucide-react';
import { usePush } from '../hooks/usePush';
import { useTheme, ThemeMode } from '../hooks/useTheme';
import { Meal, Symptom, UserSession, Exercise } from '../lib/types';
import { localStore } from '../lib/storage';
import { testAndSelectGeminiModel } from '../lib/gemini';

const ReportIssueModal = React.lazy(() => import('./ReportIssueModal').then((m) => ({ default: m.ReportIssueModal })));
const AdminDebugModal = React.lazy(() => import('./AdminDebugModal').then((m) => ({ default: m.AdminDebugModal })));
const DoctorSummaryModal = React.lazy(() => import('./DoctorSummaryModal').then((m) => ({ default: m.DoctorSummaryModal })));
const ManageFoodsModal = React.lazy(() => import('./ManageFoodsModal').then((m) => ({ default: m.ManageFoodsModal })));
import { CustomizeSymptomsModal } from './CustomizeSymptomsModal';
import { ScoreRepairPreviewSheet } from './ScoreRepairPreviewSheet';
import { SymptomRepair } from '../lib/scoreRepair';
import { APP_VERSION } from '../version';

export interface ReminderSlot {
  id: string;
  label: string;
  time: string;
  enabled: boolean;
}

const DEFAULT_REMINDER_SCHEDULE: ReminderSlot[] = [
  { id: 'morning', label: 'Morning Gut & Stool Check-In', time: '08:00', enabled: true },
  { id: 'breakfast', label: 'Breakfast Meal Log', time: '09:00', enabled: true },
  { id: 'lunch', label: 'Lunch Meal Log', time: '13:00', enabled: true },
  { id: 'dinner', label: 'Dinner Meal Log', time: '19:30', enabled: true },
  { id: 'evening', label: 'Evening Gut Reflection & Symptoms', time: '21:30', enabled: true },
];

interface SettingsViewProps {
  onClearData: () => void;
  onLoadSampleData: () => void;
  onRefreshCloud: () => Promise<void>;
  onRestoreRecoverableData?: () => Promise<number>;
  onApplySymptomRepairs?: (repairs: SymptomRepair[]) => Promise<{ repairedCount: number; cloudPendingCount: number }>;
  isSyncing: boolean;
  syncStatus: 'cloud' | 'local';
  pendingSyncCount?: number;
  meals: Meal[];
  symptoms: Symptom[];
  exercises?: Exercise[];
  user?: UserSession | null;
  onLogout?: () => void;
  onUpdateAccountName?: (newName: string) => Promise<boolean>;
  onDeleteAccount?: () => Promise<boolean>;
  onOpenPwaInstructions?: () => void;
  onTriggerUpdateOverlayPreview?: () => void;
}

export const SettingsView: React.FC<SettingsViewProps> = React.memo(({
  onClearData,
  onLoadSampleData,
  onRefreshCloud,
  onRestoreRecoverableData,
  onApplySymptomRepairs,
  isSyncing,
  syncStatus,
  pendingSyncCount = 0,
  meals,
  symptoms,
  exercises = [],
  user,
  onLogout,
  onUpdateAccountName,
  onDeleteAccount,
  onOpenPwaInstructions,
  onTriggerUpdateOverlayPreview,
}) => {
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const [isRestoringVault, setIsRestoringVault] = useState(false);

  const handleRestoreVault = async () => {
    setIsRestoringVault(true);
    if (onRestoreRecoverableData) {
      const restored = await onRestoreRecoverableData();
      await onRefreshCloud();
      setActionNotice(restored > 0 ? `Restored ${restored} missing entries from local vault!` : 'Local vault is fully up to date.');
    }
    setIsRestoringVault(false);
    setTimeout(() => setActionNotice(null), 4000);
  };

  // Profile State
  const [profile, setProfile] = useState(() => localStore.getUserProfile(user?.userId, user?.accountId));
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(profile.name || user?.accountId || (user?.userId === 'usr_harry' ? 'Harry Elliott' : 'User'));
  const [isSavingName, setIsSavingName] = useState(false);

  // Danger Zone Modals
  const [showClearModal, setShowClearModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showManageFoodsModal, setShowManageFoodsModal] = useState(false);
  const [isDoctorSummaryOpen, setIsDoctorSummaryOpen] = useState(false);
  const [isScoreCheckOpen, setIsScoreCheckOpen] = useState(false);
  const [isCustomizeSymptomsOpen, setIsCustomizeSymptomsOpen] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [clearConfirmText, setClearConfirmText] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Admin & Ticket States
  const isAdmin = (user?.accountId || '').toLowerCase() === 'harry' || user?.userId === 'usr_harry';
  const [isRemindersExpanded, setIsRemindersExpanded] = useState(false);

  // FODMAP Tracking State
  const [fodmapEnabled, setFodmapEnabled] = useState(() => localStore.getFodmapTrackingEnabled(user?.userId));

  useEffect(() => {
    const currentProfile = localStore.getUserProfile(user?.userId, user?.accountId);
    setProfile(currentProfile);
    setNameInput(currentProfile.name || user?.accountId || (user?.userId === 'usr_harry' ? 'Harry Elliott' : 'User'));
    setFodmapEnabled(localStore.getFodmapTrackingEnabled(user?.userId));
  }, [user?.userId, user?.accountId]);

  // Gemini AI Key State
  const [geminiApiKeyInput, setGeminiApiKeyInput] = useState(() => localStore.getGeminiApiKey());
  const [activeGeminiModel, setActiveGeminiModel] = useState(() => localStore.getGeminiModel());
  const [isTestingGemini, setIsTestingGemini] = useState(false);
  const [geminiTestStatus, setGeminiTestStatus] = useState<string | null>(null);

  const handleSaveAndTestGemini = async () => {
    const key = geminiApiKeyInput.trim();
    if (!key) {
      localStore.saveGeminiApiKey('');
      setGeminiTestStatus('API key cleared.');
      setActionNotice('Gemini API key cleared');
      setTimeout(() => setActionNotice(null), 2500);
      return;
    }

    setIsTestingGemini(true);
    setGeminiTestStatus('Discovering and testing supported models from Google AI Studio...');
    localStore.saveGeminiApiKey(key);

    const result = await testAndSelectGeminiModel(key);
    setIsTestingGemini(false);

    if (result.ok && result.model) {
      setActiveGeminiModel(result.model);
      setGeminiTestStatus(`Connected: ${result.model}`);
      setActionNotice('Gemini connected & verified');
    } else {
      setGeminiTestStatus(`Error: ${result.error || 'Connection failed'}`);
      setActionNotice('Gemini test failed');
    }
    setTimeout(() => setActionNotice(null), 3500);
  };

  const handleToggleFodmap = (enabled: boolean) => {
    setFodmapEnabled(enabled);
    localStore.saveFodmapTrackingEnabled(enabled, user?.userId);
    setActionNotice(enabled ? 'FODMAP tracking enabled' : 'FODMAP tracking hidden');
    setTimeout(() => setActionNotice(null), 2500);
  };

  // Reminders State
  const [reminders, setReminders] = useState<ReminderSlot[]>(() => {
    try {
      const saved = localStorage.getItem('digestlog_reminder_schedule');
      if (saved) return JSON.parse(saved);
    } catch {}
    return DEFAULT_REMINDER_SCHEDULE;
  });

  const {
    isSubscribed,
    toggleSubscription,
    sendTestNotification,
    sendBackgroundTestPush,
    syncScheduleToServer,
    isIOSDevice,
    isStandaloneMode,
    loading: pushLoading,
  } = usePush();
  const { themeMode, updateTheme } = useTheme();

  const [backgroundTestStatus, setBackgroundTestStatus] = useState<string | null>(null);
  const [isSendingBackgroundTest, setIsSendingBackgroundTest] = useState(false);

  const handleTestClosedAppPush = async () => {
    setIsSendingBackgroundTest(true);
    setBackgroundTestStatus('Scheduling background alert in 5 seconds...');
    const res = await sendBackgroundTestPush(5);
    setIsSendingBackgroundTest(false);
    if (res.success) {
      setBackgroundTestStatus('Alert scheduled! Lock your phone or close DigestLog right now to receive it in 5 seconds.');
      setTimeout(() => setBackgroundTestStatus(null), 12000);
    } else {
      setBackgroundTestStatus(res.message || 'Failed to schedule push alert');
      setTimeout(() => setBackgroundTestStatus(null), 6000);
    }
  };

  const handleAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target?.result as string;
        const next = { ...profile, avatarUrl: base64 };
        setProfile(next);
        localStore.saveUserProfile(next, user?.userId);
        setActionNotice('Profile photo updated');
        setTimeout(() => setActionNotice(null), 2500);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveName = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed) return;
    setIsSavingName(true);
    const next = { ...profile, name: trimmed };
    setProfile(next);
    localStore.saveUserProfile(next, user?.userId);

    if (onUpdateAccountName) {
      await onUpdateAccountName(trimmed);
    }

    setIsSavingName(false);
    setIsEditingName(false);
    setActionNotice('Name updated');
    setTimeout(() => setActionNotice(null), 2500);
  };

  const handleConfirmClearData = () => {
    if (clearConfirmText.trim().toUpperCase() !== 'CLEAR') return;
    onClearData();
    setShowClearModal(false);
    setClearConfirmText('');
    setActionNotice('Logged data cleared');
    setTimeout(() => setActionNotice(null), 2500);
  };

  const handleConfirmDeleteAccount = async () => {
    if (deleteConfirmText.trim().toUpperCase() !== 'DELETE') return;
    setIsDeletingAccount(true);
    try {
      if (onDeleteAccount) {
        await onDeleteAccount();
      }
      setShowDeleteModal(false);
      setDeleteConfirmText('');
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const handleUpdateReminderTime = (id: string, newTime: string) => {
    setReminders((prev) => {
      const updated = prev.map((r) => (r.id === id ? { ...r, time: newTime } : r));
      localStorage.setItem('digestlog_reminder_schedule', JSON.stringify(updated));
      return updated;
    });
    syncScheduleToServer();
    setActionNotice('Reminder updated');
    setTimeout(() => setActionNotice(null), 2000);
  };

  const handleToggleReminder = (id: string) => {
    setReminders((prev) => {
      const updated = prev.map((r) => (r.id === id ? { ...r, enabled: !r.enabled } : r));
      localStorage.setItem('digestlog_reminder_schedule', JSON.stringify(updated));
      return updated;
    });
    syncScheduleToServer();
  };

  const exportCsv = () => {
    const mealHeaders = 'id,timestamp_iso,date,meal_type,description,ingredients_tags,notes\n';
    const mealRows = meals
      .map(
        (m) =>
          `"${m.id}","${m.timestamp_iso}","${m.date}","${m.meal_type}","${m.description.replace(/"/g, '""')}","${m.ingredients_tags}","${(m.notes || '').replace(/"/g, '""')}"`
      )
      .join('\n');

    const blob = new Blob([mealHeaders + mealRows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `digestlog-meals-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportJson = () => {
    const data = {
      profile,
      meals,
      symptoms,
      exportedAt: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `digestlog-export-${new Date().toISOString().slice(0, 10)}.json`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-4 pb-28 pt-1 text-left select-none max-w-md mx-auto">
      {actionNotice && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50 px-4 py-2 bg-slate-950 dark:bg-white text-white dark:text-black rounded-2xl text-xs font-bold flex items-center space-x-2 shadow-lg animate-fade-in">
          <Check className="w-3.5 h-3.5 text-emerald-400 dark:text-emerald-600" />
          <span>{actionNotice}</span>
        </div>
      )}

      {/* ========================================================================= */}
      {/* GROUP 1: PROFILE & ACCOUNT                                                */}
      {/* ========================================================================= */}
      <div className="p-5 rounded-3xl bg-white dark:bg-[#14161F] border border-slate-200/80 dark:border-zinc-800/80 shadow-xs">
        <div className="flex items-center space-x-4 mb-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-full overflow-hidden bg-slate-100 dark:bg-zinc-800 border-2 border-slate-200 dark:border-zinc-800/80 flex items-center justify-center">
              {profile.avatarUrl ? (
                <img src={profile.avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <User className="w-8 h-8 text-slate-400 dark:text-zinc-500" />
              )}
            </div>
            <label
              htmlFor="avatar-upload"
              className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-slate-950 dark:bg-white text-white dark:text-black flex items-center justify-center cursor-pointer shadow-sm active:scale-95"
            >
              <Camera className="w-3 h-3" />
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                onChange={handleAvatarUpload}
                className="hidden"
              />
            </label>
          </div>

          <div className="flex-1 min-w-0">
            {isEditingName ? (
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  disabled={isSavingName}
                  className="px-2.5 py-1 text-xs font-bold rounded-xl bg-slate-100 dark:bg-zinc-800 text-slate-900 dark:text-white border border-slate-300 dark:border-zinc-600 focus:outline-none w-full"
                />
                <button
                  type="button"
                  onClick={handleSaveName}
                  disabled={isSavingName || !nameInput.trim()}
                  className="px-2.5 py-1 bg-slate-950 dark:bg-white text-white dark:text-black text-[11px] font-bold rounded-xl disabled:opacity-50 active:scale-95"
                >
                  {isSavingName ? '...' : 'Save'}
                </button>
              </div>
            ) : (
              <div onClick={() => setIsEditingName(true)} className="cursor-pointer group">
                <h3 className="text-base font-black text-slate-950 dark:text-white truncate">
                  {profile.name || user?.accountId || (user?.userId === 'usr_harry' ? 'Harry Elliott' : 'User')}
                </h3>
                <span className="text-[10px] text-slate-400 dark:text-zinc-500 font-semibold group-hover:underline">
                  Tap to edit name
                </span>
              </div>
            )}
            <p className="text-[11px] text-slate-500 dark:text-zinc-400 font-mono mt-0.5">
              Account: {user?.accountId || profile.accountId || 'Harry'} • ID: {user?.userId || profile.userId || 'usr_harry'}
            </p>
          </div>
        </div>

        {/* KV Sync & Offline Resilience status box */}
        <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-zinc-900/40 border border-slate-200/60 dark:border-zinc-800/80 space-y-2.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 min-w-0">
              <Database className="w-3.5 h-3.5 text-slate-500 dark:text-zinc-400 shrink-0" />
              <span className="text-xs font-bold text-slate-800 dark:text-zinc-200 truncate">
                Storage & Offline Sync
              </span>
            </div>
            <div className="flex items-center space-x-1.5">
              {pendingSyncCount > 0 && (
                <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-500/30">
                  {pendingSyncCount} Pending
                </span>
              )}
              <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full border ${
                syncStatus === 'cloud'
                  ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-500/30'
                  : 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-500/30'
              }`}>
                {syncStatus === 'cloud' ? 'Cloud Synced' : 'Offline Vault'}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-200/50 dark:border-zinc-800/50">
            <button
              type="button"
              disabled={isSyncing}
              onClick={async () => {
                await onRefreshCloud();
                setActionNotice('Synced with Cloudflare KV');
                setTimeout(() => setActionNotice(null), 2500);
              }}
              className="py-1.5 px-2.5 rounded-xl bg-white dark:bg-zinc-800 border border-slate-200 dark:border-zinc-700 text-slate-800 dark:text-zinc-200 font-bold text-[11px] hover:bg-slate-50 dark:hover:bg-zinc-700 active:scale-95 transition-all text-center flex items-center justify-center space-x-1.5 shadow-2xs"
            >
              <RefreshCw className={`w-3 h-3 ${isSyncing ? 'animate-spin' : ''}`} />
              <span>{isSyncing ? 'Syncing...' : 'Sync Cloud'}</span>
            </button>

            <button
              type="button"
              disabled={isRestoringVault}
              onClick={handleRestoreVault}
              className="py-1.5 px-2.5 rounded-xl bg-white dark:bg-zinc-800 border border-slate-200 dark:border-zinc-700 text-slate-800 dark:text-zinc-200 font-bold text-[11px] hover:bg-slate-50 dark:hover:bg-zinc-700 active:scale-95 transition-all text-center flex items-center justify-center space-x-1.5 shadow-2xs"
              title="Restores any unmerged entries from local safety vault"
            >
              <ShieldCheck className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
              <span>{isRestoringVault ? 'Restoring...' : 'Restore Vault'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* GROUP 2: DIETARY & HEALTH PREFERENCES                                     */}
      {/* ========================================================================= */}
      <div className="p-5 rounded-3xl bg-white dark:bg-[#14161F] border border-slate-200/80 dark:border-zinc-800/80 shadow-xs">
        <div className="flex items-center justify-between pb-2.5 mb-3 border-b border-slate-100 dark:border-zinc-800/80">
          <div className="flex items-center space-x-2">
            <div className="p-1.5 rounded-xl bg-slate-100 dark:bg-zinc-800 text-slate-900 dark:text-white">
              <ShieldCheck className="w-4 h-4" />
            </div>
            <div>
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500">
                Preferences
              </span>
              <h2 className="text-sm font-black text-slate-950 dark:text-white tracking-tight">
                Dietary & FODMAP Tracking
              </h2>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-zinc-900/50 border border-slate-200/70 dark:border-zinc-800/70 flex items-start justify-between gap-3">
            <div className="space-y-1 min-w-0 flex-1">
              <div className="flex items-center space-x-2">
                <span className="text-xs font-bold text-slate-900 dark:text-zinc-100">
                  Enable FODMAP Tracking
                </span>
                <span className={`text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded ${
                  fodmapEnabled
                    ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300'
                    : 'bg-slate-200 dark:bg-zinc-700 text-slate-600 dark:text-zinc-400'
                }`}>
                  {fodmapEnabled ? 'Active' : 'Hidden'}
                </span>
              </div>
              <p className="text-[11px] text-slate-500 dark:text-zinc-400 leading-snug">
                Show traffic-light ratings (Low, Med, High), single-meal stacking warnings, safe portion limits, and FODMAP search options across your journal.
              </p>
            </div>

            <button
              type="button"
              role="switch"
              aria-checked={fodmapEnabled}
              onClick={() => handleToggleFodmap(!fodmapEnabled)}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none mt-0.5 ${
                fodmapEnabled ? 'bg-emerald-600' : 'bg-slate-300 dark:bg-zinc-700'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                  fodmapEnabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {/* Customize Symptom Sliders */}
          <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-zinc-900/50 border border-slate-200/70 dark:border-zinc-800/70 flex items-start justify-between gap-3">
            <div className="space-y-1 min-w-0 flex-1">
              <div className="flex items-center space-x-2">
                <Sliders className="w-4 h-4 text-blue-500" />
                <span className="text-xs font-bold text-slate-900 dark:text-zinc-100">
                  Customize Symptom Sliders
                </span>
              </div>
              <p className="text-[11px] text-slate-500 dark:text-zinc-400 leading-snug">
                Choose which symptom sliders appear in your journal, filter out unneeded ones, or add custom symptoms (e.g. Brain Fog, Fatigue).
              </p>
            </div>

            <button
              type="button"
              onClick={() => setIsCustomizeSymptomsOpen(true)}
              className="px-3.5 py-1.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white dark:bg-zinc-100 dark:hover:bg-white dark:text-zinc-950 text-xs font-semibold shrink-0 transition-colors shadow-xs"
            >
              Configure
            </button>
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* GROUP 2.5: GOOGLE GEMINI AI & VISION ENGINE                               */}
      {/* ========================================================================= */}
      <div className="p-5 rounded-3xl bg-white dark:bg-[#14161F] border border-slate-200/80 dark:border-zinc-800/80 shadow-xs">
        <div className="flex items-center justify-between pb-2.5 mb-3 border-b border-slate-100 dark:border-zinc-800/80">
          <div className="flex items-center space-x-2">
            <div className="p-1.5 rounded-xl bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400">
              <Sparkles className="w-4 h-4" />
            </div>
            <div>
              <span className="text-[10px] font-black uppercase tracking-wider text-purple-600 dark:text-purple-400">
                AI Engine
              </span>
              <h2 className="text-sm font-black text-slate-950 dark:text-white tracking-tight">
                Google Gemini & Vision Studio
              </h2>
            </div>
          </div>
          <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800/40">
            {activeGeminiModel ? activeGeminiModel.replace(/^v1beta\//, '') : 'gemini-3.5-flash-lite'}
          </span>
        </div>

        <p className="text-xs text-slate-600 dark:text-zinc-400 font-medium leading-relaxed mb-4">
          Connect your Google AI Studio API key for instant direct-to-Gemini photo scanning, meal decomposition, and FODMAP analysis with zero proxy latency.
        </p>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-800 dark:text-zinc-200 flex items-center justify-between">
              <span>Google Gemini API Key</span>
              <a
                href="https://aistudio.google.com/app/apikey"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] font-bold text-purple-600 dark:text-purple-400 hover:underline inline-flex items-center space-x-1"
              >
                <span>Get Free Key</span>
                <ExternalLink className="w-3 h-3 ml-0.5" />
              </a>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                <Key className="w-4 h-4" />
              </div>
              <input
                type="password"
                value={geminiApiKeyInput}
                onChange={(e) => setGeminiApiKeyInput(e.target.value)}
                placeholder="AIzaSy..."
                className="w-full pl-9 pr-3 py-2.5 text-xs font-mono rounded-2xl bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500/20"
              />
            </div>
          </div>

          {geminiTestStatus && (
            <div className={`p-3 rounded-2xl text-xs font-medium border flex items-start space-x-2 ${
              geminiTestStatus.startsWith('Connected')
                ? 'bg-emerald-50 dark:bg-emerald-950/20 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/40'
                : geminiTestStatus.startsWith('Error')
                ? 'bg-rose-50 dark:bg-rose-950/20 text-rose-800 dark:text-rose-300 border-rose-200 dark:border-rose-800/40'
                : 'bg-slate-100 dark:bg-zinc-900 text-slate-700 dark:text-zinc-300 border-slate-200 dark:border-zinc-800'
            }`}>
              {geminiTestStatus.startsWith('Connected') ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
              ) : (
                <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              )}
              <div className="flex-1 break-words">
                {geminiTestStatus}
              </div>
            </div>
          )}

          <div className="flex items-center space-x-2 pt-1">
            <button
              type="button"
              onClick={handleSaveAndTestGemini}
              disabled={isTestingGemini}
              className="flex-1 py-2.5 px-4 rounded-2xl bg-purple-600 hover:bg-purple-700 active:scale-[0.98] text-white text-xs font-bold transition-all disabled:opacity-50 flex items-center justify-center space-x-2 shadow-xs shadow-purple-500/20"
            >
              {isTestingGemini ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  <span>Verifying Models...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>Save & Test Connection</span>
                </>
              )}
            </button>
            {geminiApiKeyInput && (
              <button
                type="button"
                onClick={() => {
                  setGeminiApiKeyInput('');
                  localStore.saveGeminiApiKey('');
                  setGeminiTestStatus('API Key removed.');
                }}
                className="py-2.5 px-3 rounded-2xl bg-slate-100 dark:bg-zinc-800 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950/30 text-slate-600 dark:text-zinc-400 text-xs font-bold transition-colors"
                title="Clear API Key"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* GROUP 3: NOTIFICATIONS                                                    */}
      {/* ========================================================================= */}
      <div className="p-5 rounded-3xl bg-white dark:bg-[#14161F] border border-slate-200/80 dark:border-zinc-800/80 shadow-xs">
        <div className="flex items-center justify-between pb-2.5 mb-3 border-b border-slate-100 dark:border-zinc-800/80">
          <div className="flex items-center space-x-2">
            <div className="p-1.5 rounded-xl bg-slate-100 dark:bg-zinc-800 text-slate-900 dark:text-white">
              <Bell className="w-4 h-4" />
            </div>
            <div>
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500">
                Alerts
              </span>
              <h2 className="text-sm font-black text-slate-950 dark:text-white tracking-tight">
                Push Notifications
              </h2>
            </div>
          </div>

          <button
            type="button"
            disabled={pushLoading}
            onClick={toggleSubscription}
            className={`px-3 py-1.5 rounded-xl text-xs font-black transition-all ${
              isSubscribed
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'bg-slate-100 dark:bg-zinc-800 text-slate-700 dark:text-zinc-300'
            }`}
          >
            {isSubscribed ? 'ENABLED' : 'ENABLE'}
          </button>
        </div>

        <p className="text-xs text-slate-600 dark:text-zinc-400 font-medium leading-relaxed mb-3">
          Get scheduled alerts for meal logs, morning transit checks, and Bristol Stool prompts even when the app is closed.
        </p>

        {/* iOS Home Screen requirement banner if on iOS browser outside standalone mode */}
        {!Capacitor.isNativePlatform() && isIOSDevice && !isStandaloneMode && (
          <div className="mb-3 p-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-900 dark:text-amber-200">
            <div className="flex items-center space-x-2 mb-1.5">
              <Share2 className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
              <span className="text-xs font-bold uppercase tracking-wider">iOS Home Screen Required</span>
            </div>
            <p className="text-[11px] leading-relaxed text-amber-800 dark:text-amber-300">
              Apple requires web apps to be added to your Home Screen to receive notifications when closed:
            </p>
            <ol className="text-[11px] mt-2 space-y-1 list-decimal list-inside text-amber-800 dark:text-amber-300 font-medium">
              <li>Tap the <span className="font-bold">Share</span> button at the bottom of Safari.</li>
              <li>Select <span className="font-bold">Add to Home Screen</span>.</li>
              <li>Open DigestLog from your home screen and tap ENABLE.</li>
            </ol>
          </div>
        )}

        {isSubscribed && (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => { sendTestNotification(); }}
                className="py-2 px-2.5 rounded-xl bg-slate-50 dark:bg-zinc-900/40 border border-slate-200 dark:border-zinc-800/80 text-slate-700 dark:text-zinc-300 font-bold text-xs hover:bg-slate-100 dark:hover:bg-zinc-800/50 active:scale-95 transition-all text-center truncate"
              >
                Instant Test
              </button>

              <button
                type="button"
                disabled={isSendingBackgroundTest}
                onClick={handleTestClosedAppPush}
                className="py-2 px-2.5 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-950 font-bold text-xs hover:opacity-90 active:scale-95 transition-all text-center truncate"
              >
                {isSendingBackgroundTest ? 'Scheduling...' : 'Test Closed-App (5s)'}
              </button>
            </div>

            {backgroundTestStatus && (
              <div className="p-2.5 rounded-xl bg-slate-100 dark:bg-zinc-800/80 border border-slate-200 dark:border-zinc-700 text-[11px] text-slate-800 dark:text-zinc-200 font-medium leading-normal animate-fade-in">
                {backgroundTestStatus}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ========================================================================= */}
      {/* GROUP 3: REMINDER TIMES (COLLAPSIBLE)                                      */}
      {/* ========================================================================= */}
      <div className="p-5 rounded-3xl bg-white dark:bg-[#14161F] border border-slate-200/80 dark:border-zinc-800/80 shadow-xs transition-all">
        <button
          type="button"
          onClick={() => setIsRemindersExpanded((prev) => !prev)}
          className="w-full flex items-center justify-between pb-2.5 mb-1 text-left focus:outline-none"
        >
          <div className="flex items-center space-x-2">
            <div className="p-1.5 rounded-xl bg-slate-100 dark:bg-zinc-800 text-slate-900 dark:text-white">
              <Clock className="w-4 h-4" />
            </div>
            <div>
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500">
                Schedule
              </span>
              <h2 className="text-sm font-black text-slate-950 dark:text-white tracking-tight">
                Reminder Times
              </h2>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="px-2.5 py-0.5 rounded-full bg-slate-100 dark:bg-zinc-800 text-[10px] font-black text-slate-600 dark:text-zinc-400">
              {reminders.filter((r) => r.enabled).length} Active
            </span>
            {isRemindersExpanded ? (
              <ChevronUp className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            )}
          </div>
        </button>

        {!isRemindersExpanded ? (
          <div className="pt-2 text-[11px] text-slate-500 dark:text-zinc-400 font-medium flex items-center justify-between">
            <span className="truncate pr-2">
              {reminders
                .filter((r) => r.enabled)
                .map((r) => `${r.time}`)
                .join(' · ') || 'No active reminders'}
            </span>
            <span className="text-xs font-bold text-teal-600 dark:text-teal-400 hover:underline shrink-0">
              Customize
            </span>
          </div>
        ) : (
          <div className="space-y-2 pt-2.5 animate-fade-in border-t border-slate-100 dark:border-zinc-800/80">
            {reminders.map((r) => (
              <div
                key={r.id}
                className="p-3 rounded-2xl bg-slate-50 dark:bg-zinc-900/40 border border-slate-200/60 dark:border-zinc-800/80 flex items-center justify-between"
              >
                <div className="flex items-center space-x-2.5 min-w-0 pr-2">
                  <input
                    type="checkbox"
                    checked={r.enabled}
                    onChange={() => handleToggleReminder(r.id)}
                    className="w-4 h-4 rounded-md accent-slate-950 dark:accent-white"
                  />
                  <span className="text-xs font-bold text-slate-900 dark:text-white truncate">
                    {r.label}
                  </span>
                </div>

                <input
                  type="time"
                  value={r.time}
                  disabled={!r.enabled}
                  onChange={(e) => handleUpdateReminderTime(r.id, e.target.value)}
                  className="px-2 py-1 bg-white dark:bg-zinc-800 border border-slate-200 dark:border-zinc-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white focus:outline-none disabled:opacity-40"
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ========================================================================= */}
      {/* GROUP 4: APPEARANCE                                                       */}
      {/* ========================================================================= */}
      <div className="p-5 rounded-3xl bg-white dark:bg-[#14161F] border border-slate-200/80 dark:border-zinc-800/80 shadow-xs">
        <div className="flex items-center space-x-2 pb-2.5 mb-3 border-b border-slate-100 dark:border-zinc-800/80">
          <div className="p-1.5 rounded-xl bg-slate-100 dark:bg-zinc-800 text-slate-900 dark:text-white">
            <Sun className="w-4 h-4" />
          </div>
          <div>
            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500">
              Interface
            </span>
            <h2 className="text-sm font-black text-slate-950 dark:text-white tracking-tight">
              Appearance
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          {(
            [
              { mode: 'auto' as ThemeMode, label: 'System', icon: Smartphone },
              { mode: 'light' as ThemeMode, label: 'Light', icon: Sun },
              { mode: 'dark' as ThemeMode, label: 'Dark', icon: Moon },
            ] as const
          ).map(({ mode, label, icon: Icon }) => (
            <button
              key={mode}
              type="button"
              onClick={() => updateTheme(mode)}
              className={`p-3 rounded-2xl flex flex-col items-center justify-center space-y-1.5 transition-all ${
                themeMode === mode
                  ? 'bg-slate-950 dark:bg-white text-white dark:text-black font-black shadow-xs'
                  : 'bg-slate-50 dark:bg-zinc-800/80 text-slate-600 dark:text-zinc-400 border border-slate-200/70 dark:border-zinc-800/80'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="text-xs">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* GROUP 5: DATA MANAGEMENT & EXPORT                                         */}
      {/* ========================================================================= */}
      <div className="p-5 rounded-3xl bg-white dark:bg-[#14161F] border border-slate-200/80 dark:border-zinc-800/80 shadow-xs">
        <div className="flex items-center space-x-2 pb-2.5 mb-3 border-b border-slate-100 dark:border-zinc-800/80">
          <div className="p-1.5 rounded-xl bg-slate-100 dark:bg-zinc-800 text-slate-900 dark:text-white">
            <Download className="w-4 h-4" />
          </div>
          <div>
            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500">
              Backups & Privacy
            </span>
            <h2 className="text-sm font-black text-slate-950 dark:text-white tracking-tight">
              Data Management
            </h2>
          </div>
        </div>

        <div className="space-y-2">
          {onOpenPwaInstructions && (
            <button
              type="button"
              onClick={onOpenPwaInstructions}
              className="w-full p-3 rounded-2xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-between text-xs font-bold text-teal-800 dark:text-teal-300 hover:bg-teal-500/15 active:scale-[0.99] transition-all"
            >
              <div className="flex items-center space-x-2">
                <Smartphone className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                <span>How to Add to Home Screen (Instructions)</span>
              </div>
              <ChevronRight className="w-4 h-4 text-teal-600 dark:text-teal-400" />
            </button>
          )}

          <button
            type="button"
            onClick={() => setIsDoctorSummaryOpen(true)}
            className="w-full p-3 rounded-2xl bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200/70 dark:border-indigo-800/60 flex items-center justify-between text-xs font-bold text-indigo-900 dark:text-indigo-200 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 active:scale-[0.99] transition-all"
          >
            <div className="flex items-center space-x-2">
              <FileText className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              <span>Export Doctor Briefing (PDF / Print)</span>
            </div>
            <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-indigo-200/60 dark:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300">
              30s Consult
            </span>
          </button>

          <button
            type="button"
            onClick={exportCsv}
            className="w-full p-3 rounded-2xl bg-slate-50 dark:bg-zinc-900/40 border border-slate-200/70 dark:border-zinc-800/80 flex items-center justify-between text-xs font-bold text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-white/[0.06] active:scale-[0.99] transition-all"
          >
            <div className="flex items-center space-x-2">
              <FileSpreadsheet className="w-4 h-4 text-slate-500 dark:text-zinc-400" />
              <span>Export Meals & Ingredients (CSV)</span>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-400" />
          </button>

          <button
            type="button"
            onClick={exportJson}
            className="w-full p-3 rounded-2xl bg-slate-50 dark:bg-zinc-900/40 border border-slate-200/70 dark:border-zinc-800/80 flex items-center justify-between text-xs font-bold text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-white/[0.06] active:scale-[0.99] transition-all"
          >
            <div className="flex items-center space-x-2">
              <Download className="w-4 h-4 text-slate-500 dark:text-zinc-400" />
              <span>Export Full Clinical Backup (JSON)</span>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-400" />
          </button>

          <button
            type="button"
            onClick={() => setIsScoreCheckOpen(true)}
            className="w-full p-3 rounded-2xl bg-slate-50 dark:bg-zinc-900/40 border border-slate-200/70 dark:border-zinc-800/80 flex items-center justify-between text-xs font-bold text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-white/[0.06] active:scale-[0.99] transition-all"
          >
            <div className="flex items-center space-x-2">
              <ShieldCheck className="w-4 h-4 text-slate-500 dark:text-zinc-400" />
              <span>Check & Restore Symptom Scores</span>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-400" />
          </button>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* GROUP: ADMINISTRATOR & DIAGNOSTICS (HARRY ONLY)                           */}
      {/* ========================================================================= */}
      {isAdmin && (
        <div className="p-5 rounded-3xl bg-slate-900 border border-emerald-500/40 shadow-md text-white">
          <div className="flex items-center justify-between pb-2.5 mb-3 border-b border-slate-800">
            <div className="flex items-center space-x-2">
              <div className="p-1.5 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                <Cpu className="w-4 h-4" />
              </div>
              <div>
                <span className="text-[10px] font-black uppercase tracking-wider text-emerald-400">
                  Administrator
                </span>
                <h2 className="text-sm font-black text-white tracking-tight">
                  Admin & Diagnostics
                </h2>
              </div>
            </div>
            <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[9px] font-black uppercase">
              Harry
            </span>
          </div>

          <p className="text-xs text-slate-300 leading-relaxed font-medium mb-3">
            Inspect live test users, logs recorded across accounts, Gemini AI key usage & counters, and all inputted bugs in the ticket system.
          </p>

          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setShowAdminModal(true)}
              className="w-full py-3 px-4 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs shadow-md active:scale-95 transition flex items-center justify-center space-x-2"
            >
              <Cpu className="w-4 h-4" />
              <span>Open Admin Dashboard & Bug Tracker</span>
            </button>
            <button
              type="button"
              onClick={() => setShowManageFoodsModal(true)}
              className="w-full py-3 px-4 rounded-2xl bg-slate-800 hover:bg-slate-700 text-white font-black text-xs shadow-md active:scale-95 transition flex items-center justify-center space-x-2 border border-slate-700"
            >
              <Database className="w-4 h-4 text-emerald-400" />
              <span>Manage Food & Ingredient Database</span>
            </button>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* GROUP: REPORT PROBLEM & FEEDBACK (FOR ALL USERS)                          */}
      {/* ========================================================================= */}
      <div className="p-5 rounded-3xl bg-white dark:bg-[#14161F] border border-amber-200/80 dark:border-amber-900/40 shadow-xs">
        <div className="flex items-center justify-between pb-2.5 mb-3 border-b border-amber-100 dark:border-amber-950/40">
          <div className="flex items-center space-x-2">
            <div className="p-1.5 rounded-xl bg-amber-50 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400">
              <Bug className="w-4 h-4" />
            </div>
            <div>
              <span className="text-[10px] font-black uppercase tracking-wider text-amber-600 dark:text-amber-400">
                Feedback & Testing
              </span>
              <h2 className="text-sm font-black text-slate-950 dark:text-white tracking-tight">
                Report a Problem or Bug
              </h2>
            </div>
          </div>
          <span className="px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 text-[9px] font-black uppercase">
            QA Support
          </span>
        </div>

        <p className="text-xs text-slate-600 dark:text-zinc-400 leading-relaxed font-medium mb-3">
          Spotted a bug, layout issue, or have a suggestion while testing? Input a ticket directly to Harry and track its resolution status.
        </p>

        <button
          type="button"
          onClick={() => setShowReportModal(true)}
          className="w-full p-3 rounded-2xl bg-amber-500/10 dark:bg-amber-500/15 border border-amber-200 dark:border-amber-900/60 flex items-center justify-between text-xs font-bold text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-500/25 active:scale-[0.99] transition-all"
        >
          <div className="flex items-center space-x-2">
            <Bug className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            <span>Submit a Bug Ticket / Feedback</span>
          </div>
          <ChevronRight className="w-4 h-4 text-amber-500" />
        </button>
      </div>

      {/* ========================================================================= */}
      {/* GROUP 6: ACCOUNT & DANGER ZONE                                            */}
      {/* ========================================================================= */}
      <div className="p-5 rounded-3xl bg-white dark:bg-[#14161F] border border-red-200/70 dark:border-red-950/50 shadow-xs">
        <div className="flex items-center space-x-2 pb-2.5 mb-3 border-b border-red-100 dark:border-red-950/40">
          <div className="p-1.5 rounded-xl bg-red-50 dark:bg-red-950/50 text-red-600 dark:text-red-400">
            <ShieldCheck className="w-4 h-4" />
          </div>
          <div>
            <span className="text-[10px] font-black uppercase tracking-wider text-red-500">
              Account Safety
            </span>
            <h2 className="text-sm font-black text-slate-950 dark:text-white tracking-tight">
              Session & Data Erasure
            </h2>
          </div>
        </div>

        <div className="space-y-2">
          {onLogout && (
            <button
              type="button"
              onClick={onLogout}
              className="w-full p-3 rounded-2xl bg-slate-50 dark:bg-zinc-900/40 border border-slate-200/70 dark:border-zinc-800/80 flex items-center justify-between text-xs font-bold text-slate-700 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-white/[0.06] active:scale-[0.99] transition-all"
            >
              <div className="flex items-center space-x-2">
                <LogOut className="w-4 h-4 text-slate-500 dark:text-zinc-400" />
                <span>Log Out ({user?.accountId || profile.name || 'Account'})</span>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400" />
            </button>
          )}

          <button
            type="button"
            onClick={() => setShowClearModal(true)}
            className="w-full p-3 rounded-2xl bg-amber-500/5 dark:bg-amber-500/10 border border-amber-200/70 dark:border-amber-900/40 flex items-center justify-between text-xs font-bold text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-500/20 active:scale-[0.99] transition-all"
          >
            <div className="flex items-center space-x-2">
              <Trash2 className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              <span>Clear Logged Meals & Symptoms</span>
            </div>
            <ChevronRight className="w-4 h-4 text-amber-400" />
          </button>

          <button
            type="button"
            onClick={() => setShowDeleteModal(true)}
            className="w-full p-3 rounded-2xl bg-red-500/10 dark:bg-red-500/15 border border-red-200 dark:border-red-900/60 flex items-center justify-between text-xs font-bold text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-500/25 active:scale-[0.99] transition-all"
          >
            <div className="flex items-center space-x-2">
              <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
              <span>Delete Account & Erase All Records</span>
            </div>
            <ChevronRight className="w-4 h-4 text-red-400" />
          </button>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* GROUP 6: ABOUT & CLINICAL EXPLANATIONS                                    */}
      {/* ========================================================================= */}
      <div className="p-5 rounded-3xl bg-white dark:bg-[#14161F] border border-slate-200/80 dark:border-zinc-800/80 shadow-xs">
        <div className="flex items-center space-x-2 pb-2.5 mb-3 border-b border-slate-100 dark:border-zinc-800/80">
          <div className="p-1.5 rounded-xl bg-slate-100 dark:bg-zinc-800 text-slate-900 dark:text-white">
            <Info className="w-4 h-4" />
          </div>
          <div>
            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500">
              Methodology
            </span>
            <h2 className="text-sm font-black text-slate-950 dark:text-white tracking-tight">
              About Transit & GI Analytics
            </h2>
          </div>
        </div>

        <div className="space-y-3 text-xs text-slate-600 dark:text-zinc-400 leading-relaxed font-medium">
          <div>
            <h4 className="font-bold text-slate-900 dark:text-white mb-1">
              Physiological Transit Lag
            </h4>
            <p>
              Food does not instantly trigger colonic symptoms. Upper gastrointestinal responses typically manifest within 1–3 hours, whereas small intestine and colonic fermentation peak between 6 and 14 hours after ingestion. Next-morning check-ins isolate dinner-to-wake delayed fermentation.
            </p>
          </div>

          <div>
            <h4 className="font-bold text-slate-900 dark:text-white mb-1">
              Disambiguation & Correlation Engine
            </h4>
            <p>
              By cross-referencing multi-day meal occurrences with subsequent discomfort ratings, the engine separates safe foundation foods from recurring irritants, eliminating false positives caused by shared meal ingredients.
            </p>
          </div>

          <div>
            <h4 className="font-bold text-slate-900 dark:text-white mb-1">
              Bristol Stool Form Scale
            </h4>
            <p>
              Stool morphology types 1–2 represent slow transit and constipation; types 3–4 represent optimal bowel transit; types 5–7 reflect rapid transit, osmotic draw, and active gut irritation.
            </p>
          </div>
        </div>
      </div>

      {/* App Version Info */}
      <div className="pt-2 pb-6 text-center">
        <p className="text-[11px] font-bold text-slate-400 dark:text-zinc-600">
          DigestLog v{APP_VERSION}
        </p>
      </div>

      {/* Clear Data Confirmation Modal with Typed Verification */}
      {showClearModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-sm p-6 rounded-3xl bg-white dark:bg-[#181A24] border border-amber-200 dark:border-amber-900/50 shadow-2xl space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-amber-100 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 flex items-center justify-center mx-auto">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div className="text-center space-y-1.5">
              <h3 className="text-base font-black text-slate-950 dark:text-white">
                Clear All Logged Data?
              </h3>
              <p className="text-xs text-slate-600 dark:text-zinc-400 leading-relaxed font-medium">
                This will delete all logged meals and symptom check-ins for this account. Your profile and account settings will be kept.
              </p>
            </div>

            <div className="space-y-1.5 pt-1">
              <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 dark:text-zinc-400 text-center">
                Type <span className="text-amber-600 dark:text-amber-400 font-black">CLEAR</span> to confirm:
              </label>
              <input
                type="text"
                value={clearConfirmText}
                onChange={(e) => setClearConfirmText(e.target.value)}
                placeholder="CLEAR"
                className="w-full text-center px-3 py-2 rounded-xl bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 text-xs font-black tracking-widest text-slate-900 dark:text-white uppercase focus:outline-none focus:border-amber-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowClearModal(false);
                  setClearConfirmText('');
                }}
                className="py-2.5 px-4 rounded-2xl bg-slate-100 dark:bg-zinc-800 text-xs font-bold text-slate-700 dark:text-zinc-300 hover:bg-slate-200 dark:hover:bg-zinc-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmClearData}
                disabled={clearConfirmText.trim().toUpperCase() !== 'CLEAR'}
                className="py-2.5 px-4 rounded-2xl bg-amber-600 hover:bg-amber-700 disabled:opacity-30 disabled:cursor-not-allowed text-xs font-bold text-white transition-colors shadow-sm"
              >
                Clear Data
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation Modal with Typed Verification */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-sm p-6 rounded-3xl bg-white dark:bg-[#181A24] border border-red-200 dark:border-red-900/50 shadow-2xl space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-red-100 dark:bg-red-950/60 text-red-600 dark:text-red-400 flex items-center justify-center mx-auto">
              <Trash2 className="w-6 h-6" />
            </div>
            <div className="text-center space-y-1.5">
              <h3 className="text-base font-black text-slate-950 dark:text-white">
                Permanently Delete Account?
              </h3>
              <p className="text-xs text-slate-600 dark:text-zinc-400 leading-relaxed font-medium">
                This will permanently erase the account <span className="font-bold text-slate-900 dark:text-white">"{user?.accountId || profile.name || 'this user'}"</span> along with all meal logs, symptom tracking, and clinical correlation models. This action cannot be undone.
              </p>
            </div>

            <div className="space-y-1.5 pt-1">
              <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 dark:text-zinc-400 text-center">
                Type <span className="text-red-600 dark:text-red-400 font-black">DELETE</span> to confirm:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                className="w-full text-center px-3 py-2 rounded-xl bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 text-xs font-black tracking-widest text-slate-900 dark:text-white uppercase focus:outline-none focus:border-red-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText('');
                }}
                disabled={isDeletingAccount}
                className="py-2.5 px-4 rounded-2xl bg-slate-100 dark:bg-zinc-800 text-xs font-bold text-slate-700 dark:text-zinc-300 hover:bg-slate-200 dark:hover:bg-zinc-700 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteAccount}
                disabled={isDeletingAccount || deleteConfirmText.trim().toUpperCase() !== 'DELETE'}
                className="py-2.5 px-4 rounded-2xl bg-red-600 hover:bg-red-700 disabled:opacity-30 disabled:cursor-not-allowed text-xs font-bold text-white transition-colors shadow-sm flex items-center justify-center space-x-1.5"
              >
                {isDeletingAccount ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <span>Delete Forever</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals wrapped in Suspense */}
      <React.Suspense fallback={null}>
        {/* User Issue / Bug Ticket Modal */}
        {showReportModal && (
          <ReportIssueModal
            isOpen={showReportModal}
            onClose={() => setShowReportModal(false)}
            user={user ?? null}
          />
        )}

        {/* Administrator Debug Modal (Harry Only) */}
        {isAdmin && showAdminModal && (
          <AdminDebugModal
            isOpen={showAdminModal}
            onClose={() => setShowAdminModal(false)}
            user={user ?? null}
            onTriggerUpdateOverlayPreview={onTriggerUpdateOverlayPreview}
            onSendBackgroundTestPush={sendBackgroundTestPush}
          />
        )}

        <ScoreRepairPreviewSheet
          isOpen={isScoreCheckOpen}
          onClose={() => setIsScoreCheckOpen(false)}
          userId={user?.userId || 'usr_harry'}
          onApply={onApplySymptomRepairs}
        />

        {/* 30-Second Doctor Consultation Brief Modal */}
        {isDoctorSummaryOpen && (
          <DoctorSummaryModal
            isOpen={isDoctorSummaryOpen}
            onClose={() => setIsDoctorSummaryOpen(false)}
            meals={meals}
            symptoms={symptoms}
            exercises={exercises}
            user={user}
          />
        )}

        {/* Customize Symptom Sliders Modal */}
        {isCustomizeSymptomsOpen && (
          <CustomizeSymptomsModal
            isOpen={isCustomizeSymptomsOpen}
            onClose={() => setIsCustomizeSymptomsOpen(false)}
          />
        )}

        {/* Manage Food & Ingredient Database Modal */}
        {showManageFoodsModal && (
          <ManageFoodsModal
            isOpen={showManageFoodsModal}
            onClose={() => setShowManageFoodsModal(false)}
          />
        )}
      </React.Suspense>
    </div>
  );
});
